import { Injectable, Injector, inject } from '@angular/core';
import {
    LOCALHOST_HOST,
    LOOPBACK_IPV4_HOST,
    PLATFORM_ROOT_DOMAIN,
} from 'app/core/constants/host.constants';
import {
    SYSTEM_ORG_ID,
    isSystemOrg,
} from 'app/core/constants/system-org.constants';
import {
    IOrganizationMembership,
    ITenantContext,
    TenantHostMode,
} from 'app/core/models/tenant.model';
import { AuthService } from 'app/core/services/auth.service';
import { MyOrganizationsService } from 'app/core/services/my-organizations.service';
import { OrgContextService } from 'app/core/services/org-context.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';

/** Subdomain labels that are never a tenant, across every deployed env (app/www/api/iam/sa/admin, with dev-/beta-/uat- prefixes). The BE has its own separate reserved list. */
const RESERVED_LABEL_PATTERN =
    /^(?:dev-|beta-|uat-)?(?:app|www|api|iam|sa|admin)$/;

// PLATFORM_ROOT_DOMAIN: recognized in ADDITION to the active env's own root
// because local dev's `tenancy.rootDomain` is `localhost` while org domains
// are still `{label}.photoflow.vn` — see host.constants.ts.

/**
 * Hostname → tenant-mode classification, pure and synchronous. Exported for
 * unit testing independent of the Angular injector.
 *
 * This is a *hint*, not a security boundary: the only thing that actually
 * decides which org a request is scoped to is the BE (JWT `org_id` claim for
 * authenticated calls; `GET /organizations/current`'s Origin-header lookup
 * pre-login — see `OrgContextService`). Nothing here is trusted server-side.
 */
export function classifyHostname(
    hostname: string | null | undefined,
    rootDomain: string
): { mode: TenantHostMode; slug: string | null } {
    const host = (hostname ?? '').toLowerCase().trim();
    if (!host) {
        return { mode: 'unknown', slug: null };
    }

    // `localhost` is an implicit root domain, so `tenant1.localhost` is a real
    // tenant host locally — NOT root. Bare `localhost`/`127.0.0.1` stay root.
    if (host === LOOPBACK_IPV4_HOST) {
        return { mode: 'root', slug: null };
    }

    const localRoots = [LOCALHOST_HOST];
    const roots = localRoots.includes(rootDomain.toLowerCase())
        ? localRoots
        : [...localRoots, rootDomain.toLowerCase()];

    for (const root of roots) {
        if (host === root || host === `www.${root}`) {
            return { mode: 'root', slug: null };
        }

        const suffix = `.${root}`;
        if (!host.endsWith(suffix)) {
            continue;
        }

        const prefix = host.slice(0, -suffix.length);
        // Single-label subdomains only; a multi-label prefix is unclassifiable rather than guessed.
        if (!prefix || prefix.includes('.')) {
            return { mode: 'unknown', slug: null };
        }

        if (RESERVED_LABEL_PATTERN.test(prefix)) {
            return { mode: 'root', slug: null };
        }

        return { mode: 'tenant', slug: prefix };
    }

    // Not under any root domain at all — not classifiable client-side.
    return { mode: 'unknown', slug: null };
}

/**
 * Builds the URL to navigate to for switching into `subdomainOrHost`. Plain
 * navigation (no token exchange): the target host's BFF has no session yet,
 * so IAM's `/connect/authorize` picks up the shared SSO cookie and silently
 * issues a new org-scoped session.
 *
 * A null/empty `subdomainOrHost` means the membership lives on the root app
 * host itself (the System org) and resolves to `environment.rootAppUrl`.
 *
 * Handles both shapes `OrganizationMembershipDto.OrgSubdomain` can carry (bare
 * label or full host, mid-migration). A full host under a platform root is
 * rewritten onto this env's root host — verbatim passthrough would navigate a
 * local session out to production; a host under no platform root is a custom
 * domain and passes through verbatim.
 *
 * `appUrlOverride` exists for tests: it pins the dev/prod rewrite, which is
 * invisible under the test env's localhost rootAppUrl.
 */
export function buildOrgUrl(
    subdomainOrHost: string | null | undefined,
    appUrlOverride?: string
): string | null {
    const value = (subdomainOrHost ?? '').trim().toLowerCase();

    let appUrl: URL;
    try {
        appUrl = new URL(appUrlOverride ?? environment.rootAppUrl);
    } catch {
        return null;
    }

    const appHost = appUrl.host;

    // Subdomain-less membership (the System org) → the root app host.
    if (!value) {
        return `${appUrl.protocol}//${appHost}`;
    }

    // Strip the app label to get the platform root host; local dev is left as-is.
    const firstDot = appHost.indexOf('.');
    const rootHost = firstDot === -1 ? appHost : appHost.slice(firstDot + 1);
    const platformRoots = [
        environment.tenancy?.rootDomain ?? PLATFORM_ROOT_DOMAIN,
        PLATFORM_ROOT_DOMAIN,
        LOCALHOST_HOST,
    ];

    // Full host or bare "localhost" — rewrite platform-rooted hosts onto the
    // env's root host, pass custom domains through verbatim.
    const hostNoPort = value.split(':')[0];
    if (
        value.includes('.') ||
        value.includes(':') ||
        hostNoPort === LOCALHOST_HOST
    ) {
        for (const root of platformRoots) {
            if (hostNoPort === root) {
                return `${appUrl.protocol}//${appHost}`;
            }
            const suffix = `.${root}`;
            if (hostNoPort.endsWith(suffix)) {
                const label = hostNoPort.slice(0, -suffix.length);
                if (!label || label.includes('.')) {
                    break;
                }
                return RESERVED_LABEL_PATTERN.test(label)
                    ? `${appUrl.protocol}//${appHost}`
                    : `${appUrl.protocol}//${label}.${rootHost}`;
            }
        }
        return `https://${value}`;
    }

    // Bare label — compose under the app host's root.
    return RESERVED_LABEL_PATTERN.test(value)
        ? `${appUrl.protocol}//${appHost}`
        : `${appUrl.protocol}//${value}.${rootHost}`;
}

/**
 * One studio, one host (approach.md D5/D8): the URL a session acting in
 * `actingOrgId` belongs on when it is open at `currentOrigin`, or null when it
 * is already there. Null too for the System org (it lives on the root app
 * host), for an org the user has no membership row for, and for a membership
 * whose stored domain maps back onto `currentOrigin` (e.g. Global, whose domain
 * is the bare platform root).
 *
 * `appUrlOverride` is passed through to `buildOrgUrl` for tests.
 */
export function actingOrgHostUrl(
    memberships: IOrganizationMembership[],
    actingOrgId: string | null | undefined,
    currentOrigin: string,
    appUrlOverride?: string
): string | null {
    if (!actingOrgId || isSystemOrg(actingOrgId)) {
        return null;
    }
    const membership = memberships.find((m) => m.orgId === actingOrgId);
    if (!membership) {
        return null;
    }
    const url = buildOrgUrl(membership.orgSubdomain, appUrlOverride);
    if (!url) {
        return null;
    }
    try {
        return new URL(url).origin === new URL(currentOrigin).origin
            ? null
            : url;
    } catch {
        return null;
    }
}

/**
 * Resolves the current tenant from the hostname. Composes three facts into
 * one `ITenantContext` stream:
 *
 * 1. the synchronous, hostname-only classification (root vs. tenant host,
 *    available before any network round-trip completes);
 * 2. the *host's* org, from `OrgContextService` (branding data; NOT the
 *    acting org);
 * 3. the *session's* org (`actingOrgId`), from `AuthService.user` — the JWT
 *    `org_id` claim as the BFF reports it. This is what the backend actually
 *    scopes every authenticated request to.
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
    private readonly _orgContextService = inject(OrgContextService);
    private readonly _authService = inject(AuthService);
    // Resolved on use: only the bootstrap host check needs the membership list.
    private readonly _injector = inject(Injector);

    /** `{env-prefix}photoflow.vn}` in every env today — see environment.*.ts. Not yet env-specific; see class doc. */
    private readonly _rootDomain =
        environment.tenancy?.rootDomain ?? PLATFORM_ROOT_DOMAIN;

    private readonly _hostnameClassification = classifyHostname(
        typeof window !== 'undefined' ? window.location.hostname : null,
        this._rootDomain
    );

    private readonly _context$ = new BehaviorSubject<ITenantContext>({
        mode: this._hostnameClassification.mode,
        hostnameSlug: this._hostnameClassification.slug,
        orgId: null,
        orgSlug: null,
        actingOrgId: null,
    });

    constructor() {
        // Layer the server-confirmed facts on top of the hostname hint as they
        // resolve. `mode`/`hostnameSlug` are fixed at construction, so each
        // subscription only ever overwrites its own fields.
        //
        // `OrgContextService` already dedupes/caches its own API call, so
        // subscribing here doesn't trigger extra requests.
        this._orgContextService.orgContext$.subscribe((orgContext) => {
            this._context$.next({
                ...this._context$.getValue(),
                orgId: orgContext?.orgId ?? null,
                orgSlug: orgContext?.orgSlug ?? null,
            });
        });

        // `organizationId` (from `me/permissions`) is preferred over the JWT `org_id`
        // claim because it survives a role rehydrate.
        this._authService.user$.subscribe((user) => {
            this._context$.next({
                ...this._context$.getValue(),
                actingOrgId: user?.organizationId || user?.org_id || null,
            });
        });
    }

    /** Reactive tenant context — combined hostname classification + server-confirmed org. */
    get context$(): Observable<ITenantContext> {
        return this._context$.asObservable();
    }

    /** Synchronous snapshot of the current tenant context. */
    get context(): ITenantContext {
        return this._context$.getValue();
    }

    /**
     * The org the session's token is scoped to — the authority for anything
     * user-facing that names the current organization. Null until a session is
     * restored. See `ITenantContext`.
     */
    get actingOrgId(): string | null {
        return this._context$.getValue().actingOrgId;
    }

    /**
     * True when the session's org and the host's org are both known and differ.
     * Normal on a root host (the BE's cross-tenant guard only runs for tenant
     * subdomains, so nothing 403s there — just never name `orgId` in the UI).
     * Fatal on a tenant host — the BE 403s `ORG_MISMATCH` on every call.
     */
    get isOrgMismatch(): boolean {
        const ctx = this._context$.getValue();
        return (
            !!ctx.actingOrgId && !!ctx.orgId && ctx.actingOrgId !== ctx.orgId
        );
    }

    /** `'root'` (personal/root-app host), `'tenant'` (a subdomain we recognize as a tenant slot), or `'unknown'`. */
    get mode(): TenantHostMode {
        return this._hostnameClassification.mode;
    }

    /** True on the root app host (or localhost) — the personal-workspace / org-picker / registration surfaces belong here per approach.md D8. */
    get isRootHost(): boolean {
        return this._hostnameClassification.mode === 'root';
    }

    /** Subdomain label parsed straight from `location.hostname`, before any server confirmation. Null off a tenant host. */
    get hostnameSlug(): string | null {
        return this._hostnameClassification.slug;
    }

    /** Emits true once `hostnameSlug`'s server-confirmed counterpart matches — i.e. we're truly on that org's domain, not just a look-alike hostname. */
    isConfirmedTenant$(): Observable<boolean> {
        return this.context$.pipe(
            map(
                (ctx) =>
                    ctx.mode === 'tenant' &&
                    ctx.orgSlug !== null &&
                    ctx.orgSlug === ctx.hostnameSlug
            )
        );
    }

    /**
     * Build the URL to navigate to for switching into `subdomainOrHost` —
     * delegates to the module-level `buildOrgUrl`, which is also the shared
     * builder for surfaces that can't inject this service (e.g. the
     * pending-invitations post-accept redirect).
     */
    buildOrgUrl(subdomainOrHost: string | null | undefined): string | null {
        return buildOrgUrl(subdomainOrHost);
    }

    /**
     * Whether `subdomainOrHost` can produce a navigable URL under this env's
     * root domain OR the platform's fixed production root. The org-picker
     * filters on this so a click can never silently no-op. A null/empty
     * `subdomainOrHost` is only navigable for the System org (the one
     * subdomain-less org, which lives at the root app host).
     */
    isNavigableOrgTarget(
        subdomainOrHost: string | null | undefined,
        orgId?: string | null
    ): boolean {
        const value = (subdomainOrHost ?? '').trim().toLowerCase();
        if (!value) {
            return isSystemOrg(orgId);
        }
        // Bare label → buildOrgUrl appends this env's root domain: navigable.
        if (!value.includes('.')) {
            return true;
        }
        // Full host → navigable under this env's own root or the platform's
        // fixed production root. The bare root domain itself (no label)
        // maps to the root app host via buildOrgUrl, so it is a valid target
        // only for the system org that lives there.
        if (value === this._rootDomain || value === PLATFORM_ROOT_DOMAIN) {
            return isSystemOrg(orgId);
        }
        return (
            value.endsWith(`.${this._rootDomain}`) ||
            value.endsWith(`.${PLATFORM_ROOT_DOMAIN}`)
        );
    }

    /**
     * Navigate the browser into another org's host. NOT a bare
     * `window.location.href`: the BFF session cookie is domain-scoped, so a
     * plain navigation would carry THIS session's cookie onto the target
     * host and 403 as `ORG_MISMATCH`. Instead this reuses the same BFF
     * hand-off as an ordinary sign-in (`AuthService.redirectToLogin`), with
     * `spaOrigin` pointed at the target org's host — the shared IAM login
     * session completes the switch silently, no token exchange needed.
     */
    switchToOrganization(
        subdomainOrHost: string | null | undefined,
        orgId?: string | null
    ): void {
        const url = this.buildOrgUrl(subdomainOrHost);
        if (url) {
            // The root host alone keeps the signed-in org (#1375), so the System org is named.
            this._authService.redirectToLogin(
                undefined,
                url,
                isSystemOrg(orgId) ? SYSTEM_ORG_ID : undefined
            );
        }
    }

    /**
     * One studio, one host: a session restored on the root host but acting in
     * a studio (e.g. a studio member who signed in at `localhost:4200`) is
     * moved onto that studio's own host (`thontm.localhost:4200`), keeping the
     * current path. Same BFF hand-off as `switchToOrganization`, so the shared
     * IAM session completes it silently. Resolves true when the browser is
     * navigating away; the caller should then stop bootstrapping.
     */
    async moveToActingOrgHost(): Promise<boolean> {
        if (!this.isRootHost || !this.actingOrgId) {
            return false;
        }
        const memberships = await firstValueFrom(
            this._injector.get(MyOrganizationsService).getMyOrganizations()
        );
        const url = actingOrgHostUrl(
            memberships,
            this.actingOrgId,
            globalThis.location.origin
        );
        if (!url) {
            return false;
        }
        this._authService.redirectToLogin(undefined, url);
        return true;
    }

    /** Force re-resolution of the server-confirmed org (hostname classification itself never changes without a reload). */
    refresh(): void {
        this._orgContextService.refresh();
    }
}
